import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  Download,
  Calendar,
  FileText,
  PieChart,
  Activity,
  Clock,
  MapPin,
  Users,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  exportToCSV,
  exportToJSON,
  exportToHTML,
  exportToTSV,
  exportToPDF,
  exportToTXT,
  generateGuardPerformanceData,
  generateComplianceData,
  generateLocationData,
  EXPORT_FORMATS,
  type ExportFormat
} from '../../utils/exportUtils';
import { patrolDb } from '../../lib/db';
import { useOfflineQueue } from '../../state/offlineQueue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useKioskScans } from '../../hooks/useKioskScans';
import { getLocationMeta } from '../../data/kioskLocations';
import type { RecentScan } from '../../services/scans';
import { useAuthStore } from '../../state/auth';
import { HospitalSelector } from '../../components/dashboard/HospitalSelector';
import { useStickyHospital } from '../../hooks/useStickyHospital';
import { fetchReportScans } from '../../services/reports';
import { fetchDashboardSummary } from '../../services/dashboard';
import { useTranslation } from '../../i18n';

import { Hospital, fetchHospitals } from '../../services/hospitals';

interface ReportData {
  period: string;
  completed: number;
  missed: number;
  compliance: number;
}

const UNREGISTERED_QR = "__unregistered_qr__";
const NO_OFFICER = "__no_officer__";

/** "12 min ago" / "3 h ago" / "Oct 2" */
function sinceLabel(iso: string | null | undefined, t: (k: string, f: string) => string): string {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return t('reports.minAgo', '{n} min ago').replace('{n}', String(mins));
  const h = Math.round(mins / 60);
  if (h < 48) return t('reports.hAgo', '{n} h ago').replace('{n}', String(h));
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(iso));
}

interface ScanLocation {
  checkpointId: string;
  lat: number;
  lng: number;
  count: number;
  lastScan: string;
  compliance: number;
}

// Map component to show scan locations
function ScanLocationMap({ scanLocations, lang }: { scanLocations: ScanLocation[]; lang: string }) {
  const { t } = useTranslation();
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      attributionControl: false,
      // CARTO's free GL basemaps now require an API key (raster siblings
      // watermark the map). OpenFreeMap's Positron is a keyless drop-in.
      style: "https://tiles.openfreemap.org/styles/positron",
      center: scanLocations.length > 0 ? [scanLocations[0].lng, scanLocations[0].lat] : [-84.38, 33.78],
      zoom: scanLocations.length > 0 ? 16 : 11
    });

    mapRef.current = map;

    map.on('load', () => {
      updateMarkers();
    });

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      updateMarkers();

      // Fit map to show all markers
      if (scanLocations.length > 0) {
        const lngs = scanLocations.map(loc => loc.lng);
        const lats = scanLocations.map(loc => loc.lat);
        const bounds = new maplibregl.LngLatBounds();

        scanLocations.forEach(loc => {
          bounds.extend([loc.lng, loc.lat]);
        });

        mapRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 18
        });
      }
    }
  }, [scanLocations]);

  const updateMarkers = () => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add new markers
    scanLocations.forEach((location) => {
      const el = document.createElement('div');
      el.className = 'rounded-full border-2 border-white shadow-lg cursor-pointer';
      el.style.width = '16px';
      el.style.height = '16px';
      el.style.backgroundColor = location.compliance >= 95 ? '#16a34a' :
        location.compliance >= 90 ? '#f59e0b' : '#ef4444';

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([location.lng, location.lat])
        .setPopup(
          new maplibregl.Popup({ offset: 15 }).setHTML(`
            <div class="text-sm">
              <strong>${location.checkpointId === UNREGISTERED_QR ? t('reports.unregisteredQr', 'Unregistered QR code') : location.checkpointId}</strong><br/>
              ${t('reports.popupScans', 'Scans:')} ${location.count}<br/>
              ${t('reports.popupLastScanned', 'Last scanned:')} ${sinceLabel(location.lastScan, t)}<br/>
              <span class="text-gray-600">${t('reports.popupLast', 'Last:')} ${new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(location.lastScan))}</span>
            </div>
          `)
        )
        .addTo(mapRef.current!);

      markersRef.current.push(marker);
    });
  };

  if (scanLocations.length === 0) {
    return (
      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
        <div className="text-center text-gray-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('reports.noScanLocations', 'No QR code scan locations available')}</p>
          <p className="text-xs">{t('reports.scanToSeeLocations', 'Scan QR codes to see locations on the map')}</p>
        </div>
      </div>
    );
  }

  return <div ref={mapContainer} className="h-64 w-full rounded-lg border border-gray-300" />;
}

export const ReportsPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isSupervisor = user?.role === "SUPERVISOR";
  // ADMIN and SUPERVISOR get the (role-scoped) selector and default to "all my
  // hospitals" (undefined); the report endpoints scope SUPERVISOR to their
  // assigned hospitals server-side.
  const canSelectHospital = isAdmin || isSupervisor;
  const [selectedHospitalId, setSelectedHospitalId] = useStickyHospital(
    canSelectHospital ? undefined : user?.hospital_id || undefined
  );

  // Hide any stray leaflet maps on this page
  React.useEffect(() => {
    const mapElement = document.getElementById('map');
    if (mapElement && mapElement.classList.contains('leaflet-container')) {
      mapElement.style.display = 'none';
    }
    return () => {
      if (mapElement) {
        mapElement.style.display = '';
      }
    };
  }, []);

  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month' | 'quarter'>('week');
  const [selectedReport, setSelectedReport] = useState<'overview' | 'compliance' | 'guard' | 'location'>('overview');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [scanLocations, setScanLocations] = useState<ScanLocation[]>([]);
  const [totalLocationCount, setTotalLocationCount] = useState<number>(0);
  const [reportData, setReportData] = useState<any[]>([]);
  const { drafts, load } = useOfflineQueue();
  const kioskFeed = useKioskScans(10000);
  const kioskSummary = useMemo(() => {
    try {
      return summarizeKioskData(kioskFeed.scans);
    } catch (err) {
      console.error("Failed to summarize kiosk data:", err);
      return {
        total: 0,
        ok: 0,
        invalid: 0,
        error: 0,
        exceptions: 0,
        activeKiosks: 0,
        uniqueGuards: 0,
        topLocations: []
      };
    }
  }, [kioskFeed.scans]);

  // Initialize data and hospitals
  useEffect(() => {
    void load();
    const loadHospitals = async () => {
      try {
        const list = await fetchHospitals();
        setHospitals(list);
      } catch (err) {
        console.error("Failed to load hospitals for reports", err);
      }
    };
    void loadHospitals();
  }, [load]);

  // Fetch actual scan locations from backend
  useEffect(() => {
    const fetchScanLocations = async () => {
      try {
        console.log('🔍 Fetching scan locations from backend...');

        // Calculate start_date based on period
        const now = new Date();
        let startDate = new Date();
        startDate.setHours(0, 0, 0, 0); // Default Today

        if (selectedPeriod === 'week') {
          startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
          startDate.setHours(0, 0, 0, 0);
        } else if (selectedPeriod === 'month') {
          startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
          startDate.setHours(0, 0, 0, 0);
        } else if (selectedPeriod === 'quarter') {
          startDate = new Date(now.getTime() - 89 * 24 * 60 * 60 * 1000);
          startDate.setHours(0, 0, 0, 0);
        }

        const { scans } = await fetchReportScans({
          hospital_id: selectedHospitalId,
          start_date: startDate.toISOString()
        });

        // Also fetch total locations for this hospital (or all) to get true compliance denominator
        const summary = await fetchDashboardSummary(selectedHospitalId);
        setTotalLocationCount(summary.metrics.location_count);

        setReportData(scans);
        console.log(`📊 Found ${scans.length} total scans from backend for ${selectedPeriod}`);

        // Group by checkpoint location
        const locationMap = new Map<string, ScanLocation>();

        scans.forEach(scan => {
          // A scan with no checkpoint is a QR code that isn't registered as a
          // checkpoint (or a stray scan of some other code).
          const key = scan.location_name || scan.location_id || UNREGISTERED_QR;
          const existing = locationMap.get(key) || {
            checkpointId: key,
            lat: Number(scan.gps_lat) || 33.7925,
            lng: Number(scan.gps_lng) || -84.322,
            count: 0,
            lastScan: scan.scanned_at || new Date().toISOString(),
            compliance: 100
          };

          existing.count++;
          if (scan.scanned_at && new Date(scan.scanned_at) > new Date(existing.lastScan)) {
            existing.lastScan = scan.scanned_at;
          }

          locationMap.set(key, existing);
        });

        const locations = Array.from(locationMap.values());

        // Calculate compliance based on scan frequency
        locations.forEach(loc => {
          const hoursAgo = (new Date().getTime() - new Date(loc.lastScan).getTime()) / (1000 * 60 * 60);
          // Recency bucket (drives marker colour only — this is NOT the 2-hour
          // compliance rate): <2h green, 2–8h amber, older red.
          if (hoursAgo < 2) loc.compliance = 100;
          else if (hoursAgo < 8) loc.compliance = 90;
          else loc.compliance = 85;
        });

        setScanLocations(locations);
        console.log(`✅ Set ${locations.length} scan locations in state`);
      } catch (error) {
        console.error('❌ Error fetching scan locations:', error);
      }
    };

    fetchScanLocations();
  }, [selectedPeriod, selectedHospitalId]);

  // Get data based on selected period
  const periodData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    const now = new Date();
    const dataMap = new Map<string, { completed: number; missed: number; compliance: number; timestamp: number; uniqueLocs: Set<string> }>();

    reportData.forEach(scan => {
      const date = new Date(scan.scanned_at || scan.timestamp);
      let label = "";
      let sortTimestamp = date.getTime();

      if (selectedPeriod === 'today') {
        const hour = date.getHours();
        label = hour === 0 ? '12AM' : hour < 12 ? `${hour}AM` : hour === 12 ? '12PM' : `${hour - 12}PM`;
        // Normalize timestamp to the start of the hour for sorting
        const sortDate = new Date(date);
        sortDate.setMinutes(0, 0, 0);
        sortTimestamp = sortDate.getTime();
      } else if (selectedPeriod === 'week' || selectedPeriod === 'month') {
        // Use Day + Date to ensure uniqueness (e.g., "Tue 27")
        label = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric' }).format(date);
        // Normalize to start of day
        const sortDate = new Date(date);
        sortDate.setHours(0, 0, 0, 0);
        sortTimestamp = sortDate.getTime();
      } else if (selectedPeriod === 'quarter') {
        label = new Intl.DateTimeFormat(lang, { month: 'short', year: '2-digit' }).format(date);
        // Normalize to start of month
        const sortDate = new Date(date);
        sortDate.setHours(0, 0, 0, 0);
        sortDate.setDate(1);
        sortTimestamp = sortDate.getTime();
      }

      const existing = dataMap.get(label) || { completed: 0, missed: 0, compliance: 100, timestamp: sortTimestamp, uniqueLocs: new Set<string>() };
      existing.completed += 1;
      existing.uniqueLocs.add(scan.location_id || scan.location_name || "unknown");
      dataMap.set(label, existing);
    });

    // Aggregate values
    const totalLocations = Math.max(1, totalLocationCount);
    const result = Array.from(dataMap.entries()).map(([period, data]) => ({
      period,
      ...data,
      missed: Math.max(0, totalLocations - data.uniqueLocs.size),
      compliance: Math.min(100, Math.round((data.uniqueLocs.size / totalLocations) * 100))
    }));

    // Sort chronologically by the normalized timestamp
    return result.sort((a, b) => a.timestamp - b.timestamp);
  }, [reportData, selectedPeriod, totalLocationCount, lang]);

  // Scans per OFFICER (the person signed in on the handset), not per device
  // login — device logins are shared phones and say nothing about who patrolled.
  const guardPerformance = useMemo(() => {
    if (!reportData) return [];
    type OfficerRow = {
      key: string; name: string; id: string; completed: number;
      checkpoints: Set<string>; devices: Map<string, number>; lastActive: string | null; signedIn: boolean;
    };
    const map = new Map<string, OfficerRow>();
    reportData.forEach(scan => {
      const signedIn = !!(scan.officer_id || scan.officer_name);
      const key = signedIn ? String(scan.officer_id || scan.officer_n_number || scan.officer_name) : NO_OFFICER;
      const row = map.get(key) || {
        key,
        name: signedIn ? (scan.officer_name || scan.officer_n_number || "Officer") : t('reports.noOfficerSignedIn', 'No officer signed in'),
        id: signedIn ? (scan.officer_n_number || "") : "",
        completed: 0, checkpoints: new Set<string>(), devices: new Map<string, number>(), lastActive: null, signedIn,
      };
      row.completed += 1;
      if (scan.location_id) row.checkpoints.add(scan.location_id);
      const device = scan.scanner_serial || scan.guard_id || t('reports.unknownDevice', 'Unknown device');
      row.devices.set(device, (row.devices.get(device) || 0) + 1);
      const at = scan.scanned_at || scan.timestamp;
      if (at && (!row.lastActive || new Date(at) > new Date(row.lastActive))) row.lastActive = at;
      map.set(key, row);
    });
    return Array.from(map.values())
      .map(r => ({
        key: r.key, name: r.name, id: r.id, completed: r.completed, signedIn: r.signedIn,
        checkpoints: r.checkpoints.size, lastActive: r.lastActive,
        devices: Array.from(r.devices.entries()).sort((a, b) => b[1] - a[1]).map(([device, scans]) => ({ device, scans })),
      }))
      // Named officers by scans; the "no officer signed in" row last.
      .sort((a, b) => Number(b.signedIn) - Number(a.signedIn) || b.completed - a.completed);
  }, [reportData, t]);

  const locationStats = useMemo(() => {
    if (scanLocations.length > 0) {
      return scanLocations.map(loc => ({
        location: loc.checkpointId === UNREGISTERED_QR ? t('reports.unregisteredQr', 'Unregistered QR code') : (loc.checkpointId || "Unknown"),
        lastScan: loc.lastScan,
        abbreviation: (loc.checkpointId || "??").substring(0, 2).toUpperCase(),
        scans: loc.count,
        compliance: loc.compliance,
        lat: loc.lat,
        lng: loc.lng
      }));
    }
    return [];
  }, [scanLocations]);

  const heatmapData = useMemo(() => {
    if (!reportData || locationStats.length === 0) return {};

    const hMap: Record<string, number[]> = {};

    // Initialize buckets
    locationStats.forEach(loc => {
      hMap[loc.location] = Array(8).fill(0);
    });

    reportData.forEach(scan => {
      const locKey = scan.location_name || scan.location_id || UNREGISTERED_QR;
      if (!hMap[locKey]) return;

      const date = new Date(scan.scanned_at || scan.timestamp);
      let slot = 0;

      if (selectedPeriod === 'today') {
        slot = Math.floor(date.getHours() / 3);
      } else if (selectedPeriod === 'week') {
        slot = (date.getDay() + 6) % 7; // Mon-Sun
      } else {
        slot = Math.floor(date.getDate() / 4); // Rough month bucketing
      }

      if (slot >= 0 && slot < 8) {
        hMap[locKey][slot] += 1;
      }
    });

    return hMap;
  }, [reportData, locationStats, selectedPeriod]);

  const complianceTrendData = useMemo(() => {
    if (!periodData || periodData.length === 0) return [];

    const totalLocations = Math.max(1, totalLocationCount);

    return periodData.map(d => {
      const coveragePercentage = Math.min(100, Math.round((d.uniqueLocs.size / totalLocations) * 100));
      return {
        ...d,
        compliance: coveragePercentage,
        locationCount: d.uniqueLocs.size
      };
    });
  }, [periodData, totalLocationCount]);
  const [exportCache, setExportCache] = useState(() => ({
    guards: guardPerformance,
    compliance: periodData,
    locations: locationStats,
    period: selectedPeriod,
    hospitalName: 'All Hospitals'
  }));
  const [exportType, setExportType] = useState<string>('comprehensive');

  const selectedHospitalName = useMemo(() => {
    if (!selectedHospitalId) return "All Hospitals";
    return hospitals.find(h => h.id === selectedHospitalId)?.name || "Unknown Hospital";
  }, [selectedHospitalId, hospitals]);

  const handleExport = (format: ExportFormat, type: string) => {
    let data;
    const currentSnapshot = {
      guards: guardPerformance,
      compliance: periodData,
      locations: locationStats,
      period: selectedPeriod,
      hospitalName: selectedHospitalName
    };

    switch (type) {
      case 'guard-performance':
        data = generateGuardPerformanceData(currentSnapshot.guards);
        data.filename = `officer-activity-${currentSnapshot.hospitalName.toLowerCase().replace(/\s+/g, '-')}-${currentSnapshot.period}-${new Date().toISOString().split('T')[0]}`;
        data.title = `Officer Activity Report - ${currentSnapshot.hospitalName} - ${currentSnapshot.period.toUpperCase()}`;
        break;
      case 'compliance':
        data = generateComplianceData(currentSnapshot.compliance);
        data.filename = `compliance-report-${currentSnapshot.hospitalName.toLowerCase().replace(/\s+/g, '-')}-${currentSnapshot.period}-${new Date().toISOString().split('T')[0]}`;
        data.title = `Compliance Report - ${currentSnapshot.hospitalName} - ${currentSnapshot.period.toUpperCase()}`;
        break;
      case 'location':
        data = generateLocationData(currentSnapshot.locations);
        data.filename = `location-analysis-${currentSnapshot.hospitalName.toLowerCase().replace(/\s+/g, '-')}-${currentSnapshot.period}-${new Date().toISOString().split('T')[0]}`;
        data.title = `Location Analysis Report - ${currentSnapshot.hospitalName} - ${currentSnapshot.period.toUpperCase()}`;
        break;
      case 'comprehensive':
      default:
        // Generate a comprehensive overview report
        data = {
          headers: ['Metric', 'Value', 'Context'],
          rows: [
            ['Hospital', currentSnapshot.hospitalName, 'Selected context'],
            ['Total Checkpoints Completed', currentSnapshot.compliance.reduce((sum, item) => sum + item.completed, 0), currentSnapshot.period],
            ['Total Checkpoints Missed', currentSnapshot.compliance.reduce((sum, item) => sum + item.missed, 0), currentSnapshot.period],
            ['Average Compliance Rate', `${(
              currentSnapshot.compliance.reduce((sum, item) => sum + item.compliance, 0) /
              currentSnapshot.compliance.length
            ).toFixed(1)}%`, currentSnapshot.period],
            ['Officers with scans', currentSnapshot.guards.filter((g: any) => g.signedIn).length, currentSnapshot.period],
          ],
          filename: `comprehensive-report-${currentSnapshot.hospitalName.toLowerCase().replace(/\s+/g, '-')}-${currentSnapshot.period}-${new Date().toISOString().split('T')[0]}`,
          title: `Comprehensive Security Report - ${currentSnapshot.hospitalName} - ${currentSnapshot.period.toUpperCase()}`
        };
    }

    switch (format) {
      case 'csv':
        exportToCSV(data);
        break;
      case 'tsv':
        exportToTSV(data);
        break;
      case 'json':
        exportToJSON(data);
        break;
      case 'html':
        exportToHTML(data);
        break;
      case 'pdf':
        exportToPDF(data);
        break;
      case 'txt':
        exportToTXT(data);
        break;
    }

    setShowExportModal(false);
  };

  const generateReport = (type: string) => {
    const snapshot = {
      guards: guardPerformance,
      compliance: periodData,
      locations: locationStats,
      period: selectedPeriod,
      hospitalName: selectedHospitalName
    };

    if (type.startsWith('guard-')) {
      const guardId = type.replace('guard-', '');
      const selectedGuard = guardPerformance.find((guard) => guard.key === guardId);
      setExportCache({
        ...snapshot,
        guards: selectedGuard ? [selectedGuard] : snapshot.guards
      });
      setExportType('guard-performance');
    } else {
      setExportCache(snapshot);
      setExportType(type);
    }
    setShowExportModal(true);
  };

  const maxCompleted = Math.max(1, ...periodData.map(d => d.completed));

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">{t('reports.title')}</h1>
          <p className="text-sm text-gray-600 mt-1">{t('reports.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[44px] flex-1 sm:flex-none"
          >
            <option value="today">{t('reports.periodToday', 'Today')}</option>
            <option value="week">{t('reports.periodWeek', 'This Week')}</option>
            <option value="month">{t('reports.periodMonth', 'This Month')}</option>
            <option value="quarter">{t('reports.periodQuarter', 'This Quarter')}</option>
          </select>

          {canSelectHospital && (
            <HospitalSelector
              selectedHospitalId={selectedHospitalId}
              onHospitalChange={setSelectedHospitalId}
            />
          )}

          <button
            onClick={() => {
              const tabToType: Record<string, string> = {
                'overview': 'comprehensive',
                'compliance': 'compliance',
                'guard': 'guard-performance',
                'location': 'location'
              };
              generateReport(tabToType[selectedReport] || 'comprehensive');
            }}
            className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm rounded-md transition-colors brand-green-gradient min-h-[44px]"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('reports.exportReport', 'Export Report')}</span>
            <span className="sm:hidden">{t('common.export', 'Export')}</span>
          </button>
        </div>
      </div>

      {/* Report Type Selection — horizontally scrollable on mobile */}
      <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-max md:w-fit">
        {[
          { id: 'overview', label: t('reports.tabOverview', 'Overview'), icon: BarChart3 },
          { id: 'compliance', label: t('reports.tabCompliance', 'Compliance'), icon: CheckCircle },
          { id: 'guard', label: t('reports.tabOfficerActivity', 'Officer Activity'), icon: Users },
          { id: 'location', label: t('reports.tabLocationAnalysis', 'Location Analysis'), icon: MapPin },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedReport(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-3 md:px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap min-h-[44px]",
              selectedReport === tab.id
                ? "bg-white text-gray-800 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      </div>{/* end overflow-x-auto */}

      {/* Kiosk-focused summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('reports.patrolLocations', 'Patrol locations')}</p>
            <h2 className="text-xl font-semibold text-gray-900">{t('reports.realScanData', 'Real scan data (last 24h)')}</h2>
            <p className="text-sm text-gray-600">
              {kioskFeed.loading
                ? t('reports.loadingPatrolActivity', 'Loading patrol activity…')
                : t('reports.trackingCheckpointsOfficers', '{locations} checkpoints scanned by {guards} officers.')
                    .replace('{locations}', new Intl.NumberFormat(lang).format(kioskSummary.activeKiosks))
                    .replace('{guards}', new Intl.NumberFormat(lang).format(kioskSummary.uniqueGuards))}
            </p>
          </div>
          <div className="flex gap-4">
            <SummaryBubble label={t('reports.totalScans', 'Total scans')} value={kioskSummary.total} accent="text-blue-600" />
            <SummaryBubble label={t('reports.validated', 'Validated')} value={kioskSummary.ok} accent="text-emerald-600" />
            <SummaryBubble label={t('reports.exceptions', 'Exceptions')} value={kioskSummary.exceptions} accent="text-rose-600" />
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {kioskSummary.topLocations.map((location) => (
            <div key={location.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-sm font-semibold text-gray-900">{location.name}</p>
              <p className="text-xs text-gray-500">{t('reports.scansCount', '{n} scans').replace('{n}', new Intl.NumberFormat(lang).format(location.count))} • {location.statusLabel}</p>
            </div>
          ))}
        </div>
      </div>

      {selectedReport === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('reports.checkpointsCompleted', 'Checkpoints Completed')}</p>
                  <p className="text-xl font-bold text-gray-800">{new Intl.NumberFormat(lang).format(periodData.reduce((sum, d) => sum + d.completed, 0))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('reports.missedCheckpoints', 'Missed Checkpoints')}</p>
                  <p className="text-xl font-bold text-gray-800">{new Intl.NumberFormat(lang).format(periodData.reduce((sum, d) => sum + d.missed, 0))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('reports.averageCompliance', 'Average Compliance')}</p>
                  <p className="text-xl font-bold text-gray-800">
                    {(periodData.reduce((sum, d) => sum + d.compliance, 0) / (periodData.length || 1)).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('reports.officersWithScans', 'Officers with scans')}</p>
                  <p className="text-xl font-bold text-gray-800">{new Intl.NumberFormat(lang).format(guardPerformance.filter(g => g.signedIn).length)}</p>
                  <p className="text-xs text-gray-600">{t('reports.noChange', 'No change')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Performance Chart */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 brand-text-green" />
                {selectedPeriod === 'today' ? t('reports.trendHourly', 'Hourly Performance Trends') :
                  selectedPeriod === 'week' ? t('reports.trendDaily', 'Daily Performance Trends') :
                    selectedPeriod === 'month' ? t('reports.trendWeekly', 'Weekly Performance Trends') :
                      t('reports.trendMonthly', 'Monthly Performance Trends')}
                {selectedPeriod === 'today' && (
                  <span className="text-sm font-normal text-gray-600">
                    {t('reports.asOfTime', '(as of {time})').replace('{time}', new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit' }).format(new Date()))}
                  </span>
                )}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {periodData.map((day) => (
                  <div key={day.period} className="flex items-center gap-4">
                    <div className="w-12 text-sm font-medium text-gray-600">{day.period}</div>
                    <div className="flex-1">
                      <div className="h-4 w-full rounded bg-gray-200">
                        <div
                          className="h-4 rounded bg-gradient-to-r from-indigo-500 to-blue-500"
                          style={{ width: `${Math.max(6, (day.completed / maxCompleted) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-700 w-12 text-right">{day.completed}</div>
                    {day.missed > 0 && (
                      <div className="flex items-center gap-1 text-sm text-red-600">
                        <XCircle className="w-4 h-4" />
                        <span>{t('reports.missedCount', '{n} missed').replace('{n}', new Intl.NumberFormat(lang).format(day.missed))}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedReport === 'guard' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 brand-text-green" />
                {t('reports.officerActivity', 'Officer Activity')}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {t('reports.officerActivityNote', 'Each scan is credited to the officer signed in on the device. Devices are the shared patrol phones. For the 2-hour compliance rate, see the Compliance page.')}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('reports.colOfficer', 'Officer')}</th>
                    <th className="text-right p-4 text-sm font-medium text-gray-600">{t('reports.colScans', 'Scans')}</th>
                    <th className="text-right p-4 text-sm font-medium text-gray-600">{t('reports.colCheckpoints', 'Checkpoints')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('reports.colDevices', 'Devices used (scans)')}</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">{t('reports.colLastScan', 'Last scan')}</th>
                    <th className="text-center p-4 text-sm font-medium text-gray-600">{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {guardPerformance.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-sm text-gray-500">{t('reports.noScansPeriod', 'No scans in this period.')}</td></tr>
                  )}
                  {guardPerformance.map((guard) => (
                    <tr key={guard.key} className="hover:bg-gray-50 align-top">
                      <td className="p-4">
                        <p className={cn("font-medium", guard.signedIn ? "text-gray-800" : "italic text-gray-500")}>{guard.name}</p>
                        {guard.id && <p className="text-sm text-gray-600">{guard.id}</p>}
                      </td>
                      <td className="p-4 text-right font-medium text-gray-800">{new Intl.NumberFormat(lang).format(guard.completed)}</td>
                      <td className="p-4 text-right text-gray-700">{new Intl.NumberFormat(lang).format(guard.checkpoints)}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1.5">
                          {guard.devices.map((d) => (
                            <span key={d.device} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-700">
                              <span className="font-mono">{d.device}</span>
                              <span className="text-gray-400">·</span>
                              <span className="font-medium">{new Intl.NumberFormat(lang).format(d.scans)}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600 whitespace-nowrap">
                        {guard.lastActive ? new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(guard.lastActive)) : '—'}
                      </td>
                      <td className="p-4 text-center">
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800"
                          onClick={() => generateReport(`guard-${guard.key}`)}
                        >
                          {t('reports.export', 'Export')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedReport === 'location' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <MapPin className="w-5 h-5 brand-text-green" />
                {t('reports.checkpointLocationAnalysis', 'Checkpoint Location Analysis')}
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {locationStats.map((location, index) => (
                    <div key={location.location} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-800">{location.location}</p>
                        <p className="text-sm text-gray-600">{
                          (selectedPeriod === 'today' ? t('reports.scansToday', '{n} scans today') :
                            selectedPeriod === 'week' ? t('reports.scansThisWeek', '{n} scans this week') :
                              selectedPeriod === 'month' ? t('reports.scansThisMonth', '{n} scans this month') :
                                t('reports.scansThisQuarter', '{n} scans this quarter')
                          ).replace('{n}', new Intl.NumberFormat(lang).format(location.scans))
                        }</p>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "text-sm font-semibold",
                          location.compliance >= 95 ? "text-green-600" :
                            location.compliance >= 90 ? "text-yellow-600" : "text-red-600"
                        )}>
                          {sinceLabel(location.lastScan, t)}
                        </p>
                        <p className="text-xs text-gray-600">{t('reports.lastScannedLabel', 'last scanned')}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-lg border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-medium text-gray-800 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {t('reports.locationActivityHeatmap', 'Location Activity Heatmap')}
                      </span>
                      {selectedPeriod === 'today' && (
                        <span className="text-sm font-normal text-gray-600">
                          {t('reports.currentTime', 'Current: {time}').replace('{time}', new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit' }).format(new Date()))}
                        </span>
                      )}
                    </h3>
                  </div>
                  <div className="p-4">
                    {/* Heatmap Grid with Labels */}
                    <div className="mb-4">
                      {/* Time labels across top */}
                      <div className="grid grid-cols-9 gap-1 mb-2">
                        <div></div> {/* Empty corner cell */}
                        {selectedPeriod === 'today' ?
                          (() => {
                            const now = new Date();
                            const currentHour = now.getHours();
                            const timeSlots = ['12A', '3A', '6A', '9A', '12P', '3P', '6P', '9P'];
                            const currentSlot = Math.floor(currentHour / 3);
                            // Only show time slots up to current 3-hour block
                            return timeSlots.slice(0, currentSlot + 1).concat(Array(Math.max(0, 8 - currentSlot - 1)).fill('')).slice(0, 8);
                          })().map(time => (
                            <div key={time} className="text-xs text-gray-600 text-center font-medium">{time}</div>
                          ))
                          : selectedPeriod === 'week' ?
                            [t('reports.dayMon', 'Mon'), t('reports.dayTue', 'Tue'), t('reports.dayWed', 'Wed'), t('reports.dayThu', 'Thu'), t('reports.dayFri', 'Fri'), t('reports.daySat', 'Sat'), t('reports.daySun', 'Sun'), ''].map((day, di) => (
                              <div key={`day-${di}`} className="text-xs text-gray-600 text-center font-medium">{day}</div>
                            ))
                            : selectedPeriod === 'month' ?
                              [t('reports.wk1', 'Wk1'), t('reports.wk2', 'Wk2'), t('reports.wk3', 'Wk3'), t('reports.wk4', 'Wk4'), '', '', '', ''].map((week, wi) => (
                                <div key={`wk-${wi}`} className="text-xs text-gray-600 text-center font-medium">{week}</div>
                              ))
                              :
                              [t('reports.monJan', 'Jan'), t('reports.monFeb', 'Feb'), t('reports.monMar', 'Mar'), '', '', '', '', ''].map((month, mi) => (
                                <div key={`mon-${mi}`} className="text-xs text-gray-600 text-center font-medium">{month}</div>
                              ))
                        }
                      </div>

                      {/* Heatmap with location labels */}
                      <div className="grid grid-cols-9 gap-1">
                        {locationStats.map((location, locIndex) => (
                          <React.Fragment key={`location-row-${locIndex}`}>
                            {/* Location label */}
                            <div key={`label-${location.location}`} className="text-xs text-gray-600 pr-2 flex items-center justify-end">
                              <span className="font-medium" title={location.location}>
                                {location.abbreviation}
                              </span>
                            </div>

                            {/* Activity cells for this location */}
                            {Array.from({ length: 8 }, (_, timeIndex) => {
                              const now = new Date();
                              const currentHour = now.getHours();
                              const currentSlot = Math.floor(currentHour / 3);

                              // Don't show data for future time slots
                              if (selectedPeriod === 'today' && timeIndex > currentSlot) {
                                return (
                                  <div key={`${locIndex}-${timeIndex}`} className="aspect-square"></div>
                                );
                              }

                              // Real activity intensity based on bucketing
                              const scansInSlot = (heatmapData[location.location] || [])[timeIndex] || 0;
                              // Max intensity at 3+ scans per slot, otherwise scale
                              const intensity = Math.min(1, scansInSlot / 3);

                              const getHeatmapColor = (value: number) => {
                                if (value < 0.2) return 'bg-gray-100';
                                if (value < 0.4) return 'bg-blue-100';
                                if (value < 0.6) return 'bg-blue-200';
                                if (value < 0.8) return 'bg-blue-300';
                                return 'bg-blue-500';
                              };

                              const getTimeLabel = () => {
                                if (selectedPeriod === 'today') {
                                  const times = ['12AM-3AM', '3AM-6AM', '6AM-9AM', '9AM-12PM', '12PM-3PM', '3PM-6PM', '6PM-9PM', '9PM-12AM'];
                                  return times[timeIndex];
                                } else if (selectedPeriod === 'week') {
                                  const days = [
                                    t('reports.dayMonday', 'Monday'), t('reports.dayTuesday', 'Tuesday'),
                                    t('reports.dayWednesday', 'Wednesday'), t('reports.dayThursday', 'Thursday'),
                                    t('reports.dayFriday', 'Friday'), t('reports.daySaturday', 'Saturday'),
                                    t('reports.daySunday', 'Sunday'), ''
                                  ];
                                  return days[timeIndex];
                                } else if (selectedPeriod === 'month') {
                                  return t('reports.weekN', 'Week {n}').replace('{n}', String(timeIndex + 1));
                                }
                                return t('reports.monthN', 'Month {n}').replace('{n}', String(timeIndex + 1));
                              };

                              return (
                                <div
                                  key={`${locIndex}-${timeIndex}`}
                                  className={`aspect-square rounded-sm ${getHeatmapColor(intensity)}
                                    hover:ring-2 hover:ring-blue-400 cursor-pointer transition-all
                                    ${intensity > 0.8 ? 'shadow-md' : ''}`}
                                  title={t('reports.heatmapCellTitle', '{location} - {label}: {pct}% activity')
                                    .replace('{location}', location.location)
                                    .replace('{label}', getTimeLabel())
                                    .replace('{pct}', String(Math.round(intensity * 100)))}
                                />
                              );
                            })}
                          </React.Fragment>
                        ))}

                        {/* Add empty rows if less than 7 locations */}
                        {locationStats.length < 7 && Array.from({ length: 7 - locationStats.length }, (_, i) => (
                          <React.Fragment key={`empty-row-${i}`}>
                            <div key={`empty-label-${i}`}></div>
                            {Array.from({ length: 8 }, (_, j) => (
                              <div key={`empty-${i}-${j}`} className="aspect-square"></div>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Heatmap Legend */}
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{t('reports.lessActivity', 'Less Activity')}</span>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
                        <div className="w-3 h-3 bg-blue-100 rounded-sm"></div>
                        <div className="w-3 h-3 bg-blue-200 rounded-sm"></div>
                        <div className="w-3 h-3 bg-blue-300 rounded-sm"></div>
                        <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                      </div>
                      <span>{t('reports.moreActivity', 'More Activity')}</span>
                    </div>

                    {/* Location Abbreviations Legend */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs font-medium text-gray-700 mb-2">{t('reports.locationAbbreviations', 'Location Abbreviations:')}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                        {locationStats.map(loc => (
                          <div key={loc.abbreviation} className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">{loc.abbreviation}</span>
                            <span className="text-gray-600">{loc.location}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* QR Scan Locations Map */}
                    <div className="mt-6 bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        {t('reports.qrScanLocations', 'QR Code Scan Locations')}
                        {scanLocations.length > 0 && (
                          <span className="text-xs font-normal text-gray-600 ml-2">
                            {t('reports.checkpointsLoggedGps', '({n} checkpoints logged from GPS data)').replace('{n}', new Intl.NumberFormat(lang).format(scanLocations.length))}
                          </span>
                        )}
                      </h4>
                      <ScanLocationMap scanLocations={scanLocations} lang={lang} />
                    </div>

                    {/* Map Legend */}
                    <div className="mt-3 flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">{t('reports.recencyUnder2h', 'Scanned within 2 hours')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-600">{t('reports.recency2to8h', 'Last scan 2–8 hours ago')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-600">{t('reports.recencyOver8h', 'Last scan over 8 hours ago')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedReport === 'compliance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {(periodData.reduce((sum, d) => sum + d.compliance, 0) / periodData.length).toFixed(1)}%
                </div>
                <p className="text-gray-600">{t('reports.overallComplianceRate', 'Overall Compliance Rate')}</p>
                <p className="text-sm text-green-600 mt-1">
                  {selectedPeriod === 'today' ? t('reports.deltaYesterday', '+1.5% from yesterday') :
                    selectedPeriod === 'week' ? t('reports.deltaLastWeek', '+2.1% from last week') :
                      selectedPeriod === 'month' ? t('reports.deltaLastMonth', '+1.8% from last month') :
                        t('reports.deltaLastQuarter', '+3.2% from last quarter')}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">—</div>
                <p className="text-gray-600">{t('reports.averageResponseTime', 'Average Response Time')}</p>
                <p className="text-sm text-gray-400 mt-1">{t('reports.notAvailable', 'N/A')}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700 mb-2">
                  0
                </div>
                <p className="text-gray-600">{t('reports.criticalIssuesResolved', 'Critical Issues Resolved')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('reports.noDataForPeriod', 'No data for period')}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800">{t('reports.coverageComplianceTrends', 'Coverage Compliance Trends')}</h2>
              <p className="text-xs text-gray-500">{t('reports.coverageComplianceHelp', '% of checkpoints visited at least once during this timeframe')}</p>
            </div>
            <div className="p-6">
              <div className="h-64 flex items-end gap-2">
                {complianceTrendData.map((d, i) => (
                  <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-2 group relative">
                    <div className="absolute -top-8 bg-slate-800 text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {t('reports.trendTooltip', '{pct}% ({n} locs)').replace('{pct}', String(d.compliance)).replace('{n}', new Intl.NumberFormat(lang).format(d.locationCount))}
                    </div>
                    <div
                      className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 cursor-help"
                      style={{ height: `${Math.max(6, d.compliance)}%` }}
                    />
                    <span className="text-[10px] text-gray-500 rotate-45 mt-2 origin-left whitespace-nowrap">{d.period}</span>
                  </div>
                ))}
                {complianceTrendData.length === 0 && (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <Activity className="w-8 h-8 mb-2 opacity-20" />
                    <p className="text-sm">{t('reports.noCoverageData', 'No coverage data yet')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Export Options */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="font-medium text-gray-800 mb-3">{t('reports.quickExportOptions', 'Quick Export Options')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { name: t('reports.weeklySummary', 'Weekly Summary'), type: 'comprehensive' },
            { name: t('reports.tabOfficerActivity', 'Officer Activity'), type: 'guard-performance' },
            { name: t('reports.complianceReport', 'Compliance Report'), type: 'compliance' },
            { name: t('reports.tabLocationAnalysis', 'Location Analysis'), type: 'location' },
          ].map((report) => (
            <button
              key={report.type}
              onClick={() => generateReport(report.type)}
              className="flex items-center gap-2 p-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-4 h-4 text-gray-600" />
              <span className="text-sm">{report.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('reports.exportReport', 'Export Report')}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reports.exportFormat', 'Export Format')}
                </label>
                <div className="space-y-2">
                  {EXPORT_FORMATS.map((format) => (
                    <label key={format.value} className="flex items-center gap-3 p-2 border rounded-md hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="exportFormat"
                        value={format.value}
                        checked={exportFormat === format.value}
                        onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                        className="brand-text-green"
                      />
                      <span className="text-lg">{format.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{format.label}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleExport(exportFormat, exportType)}
                className="flex-1 px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
              >
                <Download className="w-4 h-4 inline mr-2" />
                {t('reports.downloadReport', 'Download Report')}
              </button>

              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-700 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function SummaryBubble({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="text-right">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={cn("text-2xl font-semibold", accent)}>{value}</p>
    </div>
  );
}

function summarizeKioskData(scans: RecentScan[]) {
  const total = scans.length;
  const ok = scans.filter((s) => s.status === "OK").length;
  const invalid = scans.filter((s) => s.status === "INVALID").length;
  const error = scans.filter((s) => s.status === "ERROR").length;
  const locations = new Map<string, { id: string; name: string; count: number; statusLabel: string }>();

  scans.forEach((scan) => {
    // Name from the scan itself; the static kiosk map only knows demo IDs, which
    // is why real checkpoints used to show as raw UUIDs here.
    const mId = scan.location_id || UNREGISTERED_QR;
    const name = scan.location_name || (scan.location_id ? (getLocationMeta(scan.location_id)?.name || scan.location_id) : "Unregistered QR code");
    const existing = locations.get(mId) ?? { id: mId, name, count: 0, statusLabel: "No scans" };
    existing.count += 1;
    existing.statusLabel = scan.status || "UNKNOWN";
    locations.set(mId, existing);
  });

  const topLocations = Array.from(locations.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return {
    total,
    ok,
    invalid,
    error,
    exceptions: invalid + error,
    activeKiosks: locations.size,
    // People, not phones: distinct signed-in officers.
    uniqueGuards: new Set(scans.map((s) => s.officer_id || s.officer_n_number || s.officer_name).filter(Boolean)).size,
    topLocations
  };
}
